import type { QueryClient } from "@tanstack/react-query";

import { ACCOUNT_MISMATCH, ApiError, api, AUTH_REQUIRED, LICENCE_READ_ONLY, SESSION_LOCKED } from "../../api/client";
import type { Order } from "../../api/types";
import { t } from "../../i18n/i18n";
import { plural } from "../../plural";
import { licenceStore } from "../../state/licenceScope";
import { useUi } from "../../state/ui";
import { unsavedDrafts } from "../../state/unsavedDrafts";

/** File d'enregistrement de l'OPO (audit de persistance M1, M2, m2) : aucun texte tapé n'est perdu en silence.
 *
 *  - Une file par ordre (dossier ET numéro d'OPO de la rotation CENTAC) : un champ tapé dans l'OPO 1 ne peut
 *    jamais partir vers l'OPO 2, chaque file suit la version de son ordre et la resynchronise après chaque envoi.
 *  - Un envoi refusé ne perd rien : ses champs reviennent dans la file (les valeurs tapées entre-temps l'emportent),
 *    puis repartent d'eux-mêmes après un délai croissant (1, 2, 5, 10, puis 30 s). Sur conflit de version (409,
 *    412), l'ordre est relu et les champs renvoyés aussitôt avec la nouvelle version : le serveur garde les champs
 *    tapés à travers la recomposition. Ordre finalisé : la file attend la réouverture. Dossier introuvable (404) :
 *    la file attend, sans nouvel essai automatique. Texte refusé (422) : les champs repartent un par un, seul le
 *    paragraphe fautif attend sa correction, les autres sont enregistrés. Licence expirée en lecture seule (403
 *    `LICENCE_READ_ONLY`) : refus définitif jusqu'au renouvellement, la file attend sans nouvel essai et repart
 *    d'elle-même quand la licence redevient active (`resumeReadOnlyQueues`, appelé par le portail de licence).
 *  - Copie locale des champs non confirmés (une clé par dossier, OPO et champ, avec l'ordre et la version de
 *    départ), effacée à la confirmation. À l'ouverture suivante de l'ordre, elle n'est renvoyée d'office qu'avec sa
 *    version de départ : le serveur la refuse si l'utilisateur a modifié l'ordre depuis (autre poste, régénération,
 *    version rétablie, import), et la copie est alors proposée, jamais imposée (`offered`).
 *  - Passage en arrière-plan et fermeture de l'onglet : toute la file part en requête non interruptible
 *    (keepalive) avec la dernière version connue ; pendant un envoi en cours, les champs validés entre-temps partent
 *    aussi avec la version que cet envoi laissera. Le quota de 64 Kio est partagé entre toutes les files.
 *
 *  Les files vivent au niveau du module : un changement de vue, d'OPO ou de dossier ne les interrompt pas. */

export interface OrderTarget {
  exerciseId: string;
  number: number;
}

/** Délais des nouveaux essais après un échec, le dernier servant de plafond. */
export const RETRY_DELAYS_MS = [1000, 2000, 5000, 10_000, 30_000];
/** Conflits de version renvoyés aussitôt d'affilée ; au-delà, nouvel essai différé (évite une boucle si l'ordre
 *  change sans cesse ailleurs). */
const MAX_CONFLICT_RESENDS = 2;
/** Corps des requêtes keepalive : 64 Kio au plus pour toutes celles en cours ; marge pour les en-têtes. */
export const KEEPALIVE_LIMIT = 60 * 1024;
/** Copie locale d'un champ non confirmé : `generex-order-draft:<dossier>:<OPO>:<champ>`. */
export const LOCAL_PREFIX = "generex-order-draft:";

export type SaveStatus = "saved" | "saving" | "retrying" | "blocked";
/** Attente sans nouvel essai automatique : ordre finalisé, paragraphe refusé, dossier introuvable, licence expirée en
 *  lecture seule, dossier en lecture seule pour le compte (`refused`, 403 `READ_ONLY` ou `OWNER_ONLY`), session de la
 *  personne verrouillée, fermée ou expirée (`session` : la file repart à sa reconnexion, `retryOrderQueues`). */
export type BlockReason = "finalized" | "invalid" | "gone" | "read_only" | "refused" | "session";
type HoldReason = Exclude<BlockReason, "invalid">;

export interface SaveSnapshot {
  status: SaveStatus;
  /** Heure du prochain essai automatique (`retrying`). */
  retryAt: number | null;
  /** Raison de l'attente sans nouvel essai (`blocked`). */
  blocked: BlockReason | null;
  /** Message du dernier refus, traduit. */
  message: string | null;
  /** Champs pas encore confirmés par le serveur (en cours d'envoi, en attente ou refusés), valeurs les plus récentes. */
  unconfirmed: Readonly<Record<string, unknown>>;
  /** Copies locales d'une session précédente que l'ordre a dépassées : proposées, pas envoyées. */
  offered: Readonly<Record<string, unknown>>;
}

/** Stockage de la copie locale (localStorage protégé ; null : pas de copie). */
export interface LocalStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  keys(): string[];
}

/** Quota keepalive restant, partagé entre les files (rendu quand la requête aboutit ou échoue). */
export interface KeepaliveBudget {
  left: number;
}

export interface QueueDeps {
  send: (target: OrderTarget, version: number, fields: Record<string, unknown>, keepalive: boolean) => Promise<Order>;
  load: (target: OrderTarget) => Promise<Order>;
  /** Ordre renvoyé par le serveur (envoi confirmé ou relecture après conflit) : mise en cache. */
  update: (target: OrderTarget, order: Order) => void;
  /** Premier échec d'une série, ou attente sans nouvel essai : avis. */
  failed?: (queue: OrderSaveQueue, error: unknown, blocked: BlockReason | null) => void;
  /** Tout est confirmé après une série d'échecs. */
  recovered?: (queue: OrderSaveQueue) => void;
  /** Champs non confirmés d'une session précédente rétablis depuis la copie locale et enregistrés. */
  restored?: (queue: OrderSaveQueue, count: number) => void;
  storage?: LocalStore | null;
  now?: () => number;
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}

interface LocalCopy {
  value: unknown;
  /** Version de l'ordre sur laquelle la saisie s'appuie (celle avec laquelle la file l'aurait envoyée). */
  base: number | null;
  /** Identifiant de l'ordre (un ordre recréé n'est pas le même). */
  order: string | null;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const isEmpty = (o: Record<string, unknown>) => Object.keys(o).length === 0;
const bytes = (s: string) => new TextEncoder().encode(s).length;
const without = (o: Record<string, unknown>, keys: Iterable<string>) => {
  const out = { ...o };
  for (const key of keys) delete out[key];
  return out;
};

function readCopy(raw: string | null): LocalCopy | null {
  try {
    const copy = JSON.parse(raw ?? "null") as Partial<LocalCopy> | null;
    if (!copy || copy.value === undefined) return null;
    return {
      value: copy.value,
      base: typeof copy.base === "number" ? copy.base : null,
      order: typeof copy.order === "string" ? copy.order : null,
    };
  } catch {
    return null;
  }
}

/** Conflit de version (409, 412) ; ni un ordre finalisé, ni une session ouverte pour un autre compte (409
 *  `ACCOUNT_MISMATCH`, docs/17 §4.8 : arrêt, jamais un renvoi). */
const isConflict = (refusal: ApiError | null): boolean =>
  refusal !== null &&
  (refusal.status === 412 || (refusal.status === 409 && refusal.code !== "ORDER_FINALIZED" && refusal.code !== ACCOUNT_MISMATCH));

/** Copies locales rangées sous la licence en cours (audit de persistance M8 : `state/licenceScope`) ; localStorage
 *  protégé (stockage bloqué, aperçus, essais : aucune exception ne remonte). */
export const browserStore: LocalStore | null = typeof window === "undefined" ? null : licenceStore;

export class OrderSaveQueue {
  readonly target: OrderTarget;
  private deps: QueueDeps;
  private pending: Record<string, unknown> = {};
  private inFlight: Record<string, unknown> | null = null;
  /** Envoi en cours (résolu à sa réponse, quelle qu'elle soit) : le rétablissement d'un champ l'attend. */
  private flight: Promise<void> | null = null;
  /** Champs abandonnés (« Rétablir le texte généré ») pendant leur envoi : ils ne reviennent pas en file. */
  private discarded = new Set<string>();
  /** Paragraphes refusés par le serveur (422) : gardés, renvoyés à la saisie suivante ou par « Réessayer ». */
  private rejected: Record<string, unknown> = {};
  /** Copies locales dépassées par l'ordre : proposées à l'utilisateur. */
  private offered: Record<string, unknown> = {};
  /** Après un 422 sur plusieurs champs : envoi champ par champ, pour isoler le paragraphe fautif. */
  private isolate = false;
  /** Ordre de saisie des champs (le plus récent en dernier) : priorité du keepalive quand tout ne tient pas. */
  private touched = new Map<string, number>();
  private seq = 0;
  private version: number | null = null;
  private orderId: string | null = null;
  private running = false;
  /** L'envoi en cours est parti en keepalive (arrière-plan) : il survit déjà à la fermeture. */
  private inFlightKeepalive = false;
  private attempt = 0;
  private conflicts = 0;
  private timer: unknown = null;
  private retryAt: number | null = null;
  private blocked: HoldReason | null = null;
  /** Arrêt sans envoi jusqu'au rechargement de la page (changement d'utilisateur, autre compte : `haltOrderQueues`) ;
   *  ce qui attend reste dans la copie locale, sous la portée de la personne. */
  private halted = false;
  private message: string | null = null;
  private failing = false;
  private restoreDone = false;
  private restoring = false;
  /** Dernière demande de rétablissement : rejouée au retour du réseau si elle n'a pas pu aboutir. */
  private restoreArgs: { order: Order; editable: ReadonlySet<string> } | null = null;
  private listeners = new Set<() => void>();
  private snapshot: SaveSnapshot;

  constructor(target: OrderTarget, deps: QueueDeps) {
    this.target = target;
    this.deps = deps;
    this.snapshot = this.compute();
  }

  // --- Abonnement (useSyncExternalStore) ---

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.snapshot;

  private unconfirmed(): Record<string, unknown> {
    return { ...this.rejected, ...(this.inFlight ?? {}), ...this.pending };
  }

  private compute(): SaveSnapshot {
    const unconfirmed = this.unconfirmed();
    const moving = this.inFlight !== null || !isEmpty(this.pending);
    let status: SaveStatus;
    let blocked: BlockReason | null = null;
    if (isEmpty(unconfirmed)) status = "saved";
    else if (this.blocked) {
      status = "blocked";
      blocked = this.blocked;
    } else if (this.halted) {
      status = "blocked"; // arrêtée sans envoi : le texte attend dans la copie locale
      blocked = "session";
    } else if (this.timer !== null) status = "retrying";
    else if (moving) status = "saving";
    else {
      status = "blocked"; // seuls restent des paragraphes refusés
      blocked = "invalid";
    }
    const retryAt = status === "retrying" ? this.retryAt : null;
    return { status, retryAt, blocked, message: this.message, unconfirmed, offered: { ...this.offered } };
  }

  private emit() {
    this.snapshot = this.compute();
    for (const listener of this.listeners) listener();
  }

  /** Valeur non confirmée d'un champ, s'il en a une (sert de référence à l'éditeur, avant le texte du serveur). */
  peek(key: string): { has: boolean; value: unknown } {
    if (key in this.pending) return { has: true, value: this.pending[key] };
    if (this.inFlight && key in this.inFlight) return { has: true, value: this.inFlight[key] };
    if (key in this.rejected) return { has: true, value: this.rejected[key] };
    return { has: false, value: undefined };
  }

  /** Réponse de l'envoi en cours (s'il y en a un), qu'il réussisse ou non. */
  whenSent(): Promise<void> {
    return this.flight ?? Promise.resolve();
  }

  // --- Version suivie ---

  /** Ordre lu ou renvoyé par le serveur : la version suivie ne recule jamais pour un même ordre (`fresh` : relecture,
   *  qui fait foi). Version changée : les copies locales des champs non confirmés prennent la nouvelle base, celle
   *  avec laquelle la file les enverra. */
  private track(order: Order, fresh = false) {
    const before = `${this.orderId}:${this.version}`;
    if (fresh || order.id !== this.orderId || this.version === null) {
      this.orderId = order.id;
      this.version = order.version;
    } else this.version = Math.max(this.version, order.version);
    if (`${this.orderId}:${this.version}` !== before) {
      const unconfirmed = this.unconfirmed();
      for (const key of Object.keys(unconfirmed)) this.keep(key, unconfirmed[key]);
    }
  }

  // --- Copie locale ---

  private localKey(field: string) {
    return `${LOCAL_PREFIX}${this.target.exerciseId}:${this.target.number}:${field}`;
  }

  private keep(field: string, value: unknown) {
    const copy = { value, base: this.version, order: this.orderId, savedAt: Date.now() };
    this.deps.storage?.set(this.localKey(field), JSON.stringify(copy));
  }

  private forget(field: string) {
    this.deps.storage?.remove(this.localKey(field));
  }

  /** Copies locales laissées par une session précédente (onglet fermé, réseau coupé). Une copie déjà enregistrée,
   *  illisible ou d'un champ qui n'existe plus (`editable` : champs de l'éditeur) est effacée. Les autres sont
   *  renvoyées avec leur version de départ : le serveur les accepte si l'ordre n'a vu passer depuis que des
   *  recompositions automatiques, et les refuse (409) si l'utilisateur l'a modifié entre-temps (autre poste,
   *  autre onglet, régénération, version rétablie, import) ; elles sont alors proposées (`offered`), jamais
   *  imposées. Une fois par file ; ordre finalisé, ou envoi impossible (réseau) : rejoué plus tard. */
  async restore(order: Order, editable: ReadonlySet<string>): Promise<void> {
    const storage = this.deps.storage;
    this.restoreArgs = { order, editable };
    if (this.halted || this.restoreDone || this.restoring || !storage || order.status === "finalized") return;
    this.restoring = true;
    const prefix = this.localKey("");
    const groups = new Map<number, Record<string, unknown>>();
    const offered: Record<string, unknown> = {};
    for (const key of storage.keys()) {
      if (!key.startsWith(prefix)) continue;
      const field = key.slice(prefix.length);
      const copy = readCopy(storage.get(key));
      if (!copy || !editable.has(field) || same(copy.value, order.fields[field])) {
        storage.remove(key);
        continue;
      }
      if (this.peek(field).has || field in this.offered) continue; // saisie de cette session, ou déjà proposée
      if (copy.order === order.id && copy.base !== null && copy.base <= order.version) {
        groups.set(copy.base, { ...(groups.get(copy.base) ?? {}), [field]: copy.value });
      } else offered[field] = copy.value; // autre ordre (recréé) ou version inconnue : jamais d'office
    }
    let restored = 0;
    let later = false;
    for (const [base, fields] of [...groups.entries()].sort((a, b) => a[0] - b[0])) {
      try {
        const saved = await this.deps.send(this.target, base, fields, false);
        for (const key of Object.keys(fields)) if (!this.peek(key).has) this.forget(key);
        this.track(saved);
        this.deps.update(this.target, saved);
        restored += Object.keys(fields).length;
      } catch (err) {
        const refusal = err instanceof ApiError ? err : null;
        if (isConflict(refusal)) Object.assign(offered, fields);
        else if (refusal?.status === 422) {
          // Version acceptée, texte refusé : la file l'isole et garde le paragraphe fautif.
          this.enqueue(without(fields, Object.keys(fields).filter((k) => this.peek(k).has)));
          restored += Object.keys(fields).length;
        } else later = true; // réseau, service, licence (lecture seule comprise) : la copie reste, essai plus tard
      }
    }
    for (const key of Object.keys(offered)) if (this.peek(key).has) delete offered[key];
    this.offered = { ...this.offered, ...offered };
    this.restoring = false;
    this.restoreDone = !later;
    if (restored) this.deps.restored?.(this, restored);
    this.emit();
  }

  /** Rétablissement resté en suspens (réseau) : rejoué. */
  retryRestore() {
    if (!this.restoreDone && this.restoreArgs) void this.restore(this.restoreArgs.order, this.restoreArgs.editable);
  }

  /** « Rétablir ma saisie » : les copies proposées passent dans la file et l'emportent sur le texte actuel. */
  adoptOffered() {
    const fields = this.offered;
    this.offered = {};
    if (isEmpty(fields)) this.emit();
    else this.enqueue(fields);
  }

  /** « Garder le texte actuel » : les copies proposées sont effacées. */
  dropOffered() {
    for (const key of Object.keys(this.offered)) if (!this.peek(key).has) this.forget(key);
    this.offered = {};
    this.emit();
  }

  // --- File ---

  /** Ordre lu (cache, rechargement, réponse d'une autre action) ; un ordre réouvert relance les champs bloqués par
   *  la finalisation. */
  observe(order: Order) {
    this.track(order);
    if (this.blocked === "finalized" && order.status !== "finalized") this.retryNow();
    else if (!this.running && this.timer === null && !this.blocked && !isEmpty(this.pending)) void this.pump();
  }

  /** Champs validés par l'éditeur : en file, copiés sur le poste, envoyés (sauf `send: false`). Une nouvelle saisie
   *  d'un paragraphe refusé est un nouvel essai (paragraphe raccourci, par exemple). */
  enqueue(fields: Record<string, unknown>, { send = true }: { send?: boolean } = {}) {
    const keys = Object.keys(fields);
    if (!keys.length) return;
    for (const key of keys) {
      this.pending[key] = fields[key];
      delete this.rejected[key];
      delete this.offered[key];
      this.discarded.delete(key);
      this.touched.set(key, ++this.seq);
      this.keep(key, fields[key]);
    }
    if (isEmpty(this.rejected)) this.message = null;
    this.emit();
    if (send) void this.pump();
  }

  /** Champ remis à son texte généré : sa saisie non confirmée est abandonnée (elle écraserait le texte rétabli),
   *  y compris celle d'un envoi en cours, qui ne reviendra pas en file s'il échoue. Le rétablissement attend la
   *  réponse de cet envoi (`whenSent`). */
  discard(key: string) {
    const flying = this.inFlight !== null && key in this.inFlight;
    if (!(key in this.pending) && !flying && !(key in this.rejected) && !(key in this.offered)) return;
    delete this.pending[key];
    delete this.rejected[key];
    delete this.offered[key];
    if (flying) this.discarded.add(key);
    this.touched.delete(key);
    this.forget(key);
    this.emit();
  }

  /** « Réessayer » : nouvel essai immédiat, sans attendre le délai, paragraphes refusés compris. */
  retryNow() {
    if (this.halted) return;
    this.cancelTimer();
    this.blocked = null;
    this.conflicts = 0;
    this.pending = { ...this.rejected, ...this.pending };
    this.rejected = {};
    this.emit();
    void this.pump();
    this.retryRestore();
  }

  private cancelTimer() {
    if (this.timer !== null) {
      const clear = this.deps.clearTimer ?? ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>));
      clear(this.timer);
    }
    this.timer = null;
    this.retryAt = null;
  }

  /** Envoi de la file. `keepalive` (arrière-plan) : le premier envoi part en keepalive, dans le quota partagé
   *  (`budget`) ; s'il ne tient pas en entier, les champs les plus récents y partent et le reste suit en requête
   *  ordinaire. */
  async pump(keepalive = false, budget: KeepaliveBudget = { left: KEEPALIVE_LIMIT }): Promise<void> {
    if (this.halted || this.running || this.version === null || this.timer !== null || this.blocked) return;
    this.running = true;
    let first = keepalive;
    try {
      while (!isEmpty(this.pending) && !this.blocked && !this.halted && this.timer === null) {
        const version = this.version as number;
        let fields = this.pending;
        let reserved = 0;
        if (this.isolate) {
          const oldest = Object.keys(this.pending).sort((a, b) => (this.touched.get(a) ?? 0) - (this.touched.get(b) ?? 0))[0] as string;
          fields = { [oldest]: this.pending[oldest] };
        } else if (first) {
          const picked = this.pick(this.pending, version, budget);
          if (!isEmpty(picked.fields)) {
            fields = picked.fields;
            reserved = picked.size;
          }
        }
        first = false;
        this.pending = without(this.pending, Object.keys(fields));
        this.inFlight = fields;
        this.inFlightKeepalive = reserved > 0;
        this.emit();
        const request = this.deps.send(this.target, version, fields, reserved > 0);
        this.flight = request.then(
          () => undefined,
          () => undefined,
        );
        try {
          const order = await request;
          this.inFlight = null;
          this.discarded.clear();
          this.accept(order, fields);
        } catch (err) {
          this.inFlight = null;
          // Rien n'est perdu : les champs reviennent (sauf ceux abandonnés entre-temps), les valeurs tapées pendant
          // l'envoi l'emportent.
          this.pending = { ...without(fields, this.discarded), ...this.pending };
          this.discarded.clear();
          if (!(await this.handleFailure(err, fields))) break;
        } finally {
          budget.left += reserved;
          this.flight = null;
        }
      }
    } finally {
      this.running = false;
      if (isEmpty(this.pending)) this.isolate = false;
      this.emit();
    }
  }

  /** Envoi confirmé : copies locales effacées (sauf champ retapé depuis), version suivie, mise en cache. */
  private accept(order: Order, sent: Record<string, unknown>) {
    for (const key of Object.keys(sent)) {
      if (key in this.pending) continue;
      this.forget(key);
      this.touched.delete(key);
    }
    this.track(order);
    this.attempt = 0;
    this.conflicts = 0;
    if (isEmpty(this.rejected)) this.message = null;
    this.deps.update(this.target, order);
    if (this.failing && isEmpty(this.pending) && isEmpty(this.rejected)) {
      this.failing = false;
      this.deps.recovered?.(this);
    }
    this.emit();
  }

  /** Échec d'un envoi ; vrai si la file repart aussitôt (conflit résolu par relecture, paragraphe isolé). */
  private async handleFailure(err: unknown, sent: Record<string, unknown>): Promise<boolean> {
    const refusal = err instanceof ApiError ? err : null;
    if (isConflict(refusal) && this.conflicts < MAX_CONFLICT_RESENDS) {
      this.conflicts++;
      // Le serveur peut joindre la version courante à son refus : elle sert si la relecture échoue.
      const given = refusal?.detail?.version;
      const hinted = typeof given === "number" ? given : null;
      if (await this.reload()) return true;
      if (hinted !== null) {
        this.version = hinted;
        return true;
      }
    } else if (refusal?.code === "ORDER_FINALIZED") {
      // Ordre finalisé ailleurs : relu pour que la vue le montre (et désigne « Rouvrir »).
      await this.reload();
      this.block("finalized", err);
      return false;
    } else if (refusal?.code === ACCOUNT_MISMATCH) {
      // Session de ce navigateur ouverte pour un autre compte : arrêt, jamais un échec à réessayer ; la page va se
      // recharger (portail des comptes), la copie locale garde le texte sous la portée de la personne.
      this.halt();
      return false;
    } else if (refusal?.code === AUTH_REQUIRED || refusal?.code === SESSION_LOCKED) {
      // Session verrouillée, fermée ou expirée : la surcouche le dit ; la file repart à la reconnexion de la même
      // personne.
      this.block("session", err);
      return false;
    } else if (refusal?.status === 403 && (refusal.code === "READ_ONLY" || refusal.code === "OWNER_ONLY")) {
      // Dossier en lecture seule pour ce compte : aucun nouvel essai, la saisie reste gardée sur ce poste.
      this.block("refused", err);
      return false;
    } else if (refusal?.code === LICENCE_READ_ONLY) {
      // Licence expirée en lecture seule : aucun nouvel essai (il serait refusé de même) ; la copie locale garde le
      // texte, la file repart au retour de la licence.
      this.block("read_only", err);
      return false;
    } else if (refusal?.status === 404) {
      // Dossier supprimé, ou hors de l'espace de la licence : aucun nouvel essai automatique ; la copie locale reste
      // (le dossier peut revenir avec la bonne licence).
      this.block("gone", err);
      return false;
    } else if (refusal?.status === 422) {
      const keys = Object.keys(sent);
      if (keys.length > 1) {
        this.isolate = true; // un paragraphe fautif : les champs repartent un par un
        return true;
      }
      this.reject(sent, err);
      return true;
    }
    this.schedule(err);
    return false;
  }

  /** Relecture de l'ordre (après un conflit ou une finalisation) ; faux si elle échoue. */
  private async reload(): Promise<boolean> {
    try {
      const fresh = await this.deps.load(this.target);
      this.track(fresh, true);
      this.deps.update(this.target, fresh);
      return true;
    } catch {
      return false;
    }
  }

  /** Paragraphe refusé (422) : il attend sa correction, sauf s'il a été retapé pendant l'envoi (la nouvelle valeur
   *  part). Les autres champs de la file continuent. */
  private reject(sent: Record<string, unknown>, err: unknown) {
    for (const key of Object.keys(sent)) {
      if (!same(this.pending[key], sent[key])) continue;
      delete this.pending[key];
      this.rejected[key] = sent[key];
    }
    this.message = err instanceof Error ? err.message : null;
    this.failing = true;
    this.emit(); // avant l'avis : il lit le message du refus
    this.deps.failed?.(this, err, "invalid");
  }

  /** Arrêt sans envoi (voir `halted`). */
  halt() {
    if (this.halted) return;
    this.halted = true;
    this.cancelTimer();
    this.emit();
  }

  private block(reason: HoldReason, err: unknown) {
    const changed = this.blocked !== reason;
    this.blocked = reason;
    this.message = err instanceof Error ? err.message : null;
    this.failing = true;
    this.emit();
    if (changed) this.deps.failed?.(this, err, reason);
  }

  /** Nouvel essai différé : réseau, service indisponible (5xx), licence, espace plein, conflits répétés… */
  private schedule(err: unknown) {
    const hinted = err instanceof ApiError ? err.retryAfterMs : undefined;
    const delay = hinted ?? RETRY_DELAYS_MS[Math.min(this.attempt, RETRY_DELAYS_MS.length - 1)] ?? 30_000;
    this.attempt++;
    this.message = err instanceof Error ? err.message : null;
    this.retryAt = (this.deps.now ?? Date.now)() + delay;
    this.timer = (this.deps.setTimer ?? setTimeout)(() => {
      this.timer = null;
      this.retryAt = null;
      this.conflicts = 0;
      void this.pump();
    }, delay);
    this.emit();
    if (!this.failing) {
      this.failing = true;
      this.deps.failed?.(this, err, null);
    }
  }

  // --- Arrière-plan et fermeture ---

  /** Champs les plus récents de `source` qui tiennent dans le quota keepalive restant (réservé). */
  private pick(source: Record<string, unknown>, version: number, budget: KeepaliveBudget) {
    const keys = Object.keys(source).sort((a, b) => (this.touched.get(b) ?? 0) - (this.touched.get(a) ?? 0));
    const fields: Record<string, unknown> = {};
    let size = bytes(JSON.stringify({ version, fields: {} }));
    for (const key of keys) {
      const part = bytes(JSON.stringify(key)) + bytes(JSON.stringify(source[key] ?? null)) + 2;
      if (size + part > budget.left) continue;
      fields[key] = source[key];
      size += part;
    }
    if (isEmpty(fields)) return { fields, size: 0 };
    budget.left -= size;
    return { fields, size };
  }

  /** Requête keepalive parallèle à la file ; quota rendu à sa réponse. */
  private sendSide(version: number, source: Record<string, unknown>, budget: KeepaliveBudget) {
    const { fields, size } = this.pick(source, version, budget);
    if (isEmpty(fields)) return;
    const release = () => {
      budget.left += size;
    };
    this.deps.send(this.target, version, fields, true).then(
      (order) => {
        release();
        this.acceptSide(order, fields);
      },
      release, // refus ou page fermée : la file et la copie locale gardent le texte
    );
  }

  /** Onglet en arrière-plan (iPad) : la page vit encore, la file part tout de suite en keepalive ; si un envoi est
   *  déjà en route, des requêtes keepalive parallèles portent ce qui n'est pas confirmé. */
  flushHidden(budget: KeepaliveBudget = { left: KEEPALIVE_LIMIT }) {
    if (this.halted || this.blocked || this.version === null) return;
    if (this.running) {
      this.flushKeepalive(budget);
      return;
    }
    this.cancelTimer();
    void this.pump(true, budget);
  }

  /** Fermeture de l'onglet : requêtes keepalive avec la dernière version connue. Sans envoi en cours : tous les
   *  champs en attente. Pendant un envoi sur la version V, le serveur, s'il l'enregistre d'abord, refusera tout
   *  autre envoi sur V : les champs validés entre-temps partent donc aussi sur V+1 (version que laissera cet envoi),
   *  et l'ensemble sur V (au cas où l'envoi en cours serait interrompu par la fermeture). L'une des deux aboutit
   *  quel que soit l'ordre d'arrivée. Au-delà du quota, les champs les plus récents partent d'abord ; les autres
   *  restent dans la copie locale. Les paragraphes refusés (422) n'en font pas partie : ils feraient refuser tout. */
  flushKeepalive(budget: KeepaliveBudget = { left: KEEPALIVE_LIMIT }) {
    if (this.halted || this.blocked || this.version === null) return;
    // Passage en arrière-plan juste avant : son envoi keepalive porte déjà tout ce qui attend.
    if (this.inFlight && this.inFlightKeepalive && isEmpty(this.pending)) return;
    const version = this.version;
    if (this.inFlight) {
      if (!isEmpty(this.pending)) this.sendSide(version + 1, this.pending, budget);
      this.sendSide(version, { ...this.inFlight, ...this.pending }, budget);
    } else if (!isEmpty(this.pending)) this.sendSide(version, this.pending, budget);
  }

  /** Réponse d'une requête keepalive parallèle (la page a survécu) : champs confirmés retirés de la file. */
  private acceptSide(order: Order, sent: Record<string, unknown>) {
    for (const key of Object.keys(sent)) {
      if (key in this.pending && same(this.pending[key], sent[key])) {
        delete this.pending[key];
        if (!(this.inFlight && key in this.inFlight)) this.forget(key);
      }
    }
    if (order.id === this.orderId) this.track(order);
    this.deps.update(this.target, order);
    if (isEmpty(this.pending) && !this.inFlight) {
      this.cancelTimer();
      if (this.failing && isEmpty(this.rejected)) {
        this.failing = false;
        this.deps.recovered?.(this);
      }
    }
    this.emit();
  }
}

// --- Files de l'application ------------------------------------------------------------------------------------

const queues = new Map<string, OrderSaveQueue>();
/** Éditeurs ouverts : leur saisie en cours passe dans la file avant un vidage (arrière-plan, fermeture). */
const drains = new Set<() => void>();
/** Quota keepalive du document, commun à toutes les files (64 Kio pour toutes les requêtes keepalive en cours). */
const keepaliveBudget: KeepaliveBudget = { left: KEEPALIVE_LIMIT };
let listening = false;

/** Champs en cours de frappe d'un éditeur, versés dans sa file avant chaque vidage ; renvoie le désabonnement. */
export function registerDrain(drain: () => void): () => void {
  drains.add(drain);
  return () => drains.delete(drain);
}

function listen() {
  if (listening || typeof window === "undefined") return;
  listening = true;
  const drainAll = () => {
    for (const drain of drains) drain();
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "hidden") return;
    drainAll();
    for (const queue of queues.values()) queue.flushHidden(keepaliveBudget);
  });
  window.addEventListener("pagehide", () => {
    drainAll();
    for (const queue of queues.values()) queue.flushKeepalive(keepaliveBudget);
  });
  // Réseau revenu : les files en attente repartent sans attendre leur délai, les copies en suspens sont rejouées.
  window.addEventListener("online", () => {
    for (const queue of queues.values()) {
      if (queue.getSnapshot().status === "retrying") queue.retryNow();
      else queue.retryRestore();
    }
  });
}

/** Saisie en cours de frappe des éditeurs ouverts versée dans les files, donc dans leurs copies locales (perte de la
 *  licence : audit de persistance M6). */
export function drainOrderEditors(): void {
  for (const drain of drains) drain();
}

/** Poste réactivé : les files qui attendaient un nouvel essai repartent aussitôt (celles que la lecture seule
 *  arrêtait aussi). */
export function retryOrderQueues(): void {
  resumeQueues(queues.values());
}

/** Licence redevenue active après la lecture seule (renouvellement) : les files arrêtées par elle repartent, comme
 *  celles qui attendaient un nouvel essai ; les copies en suspens sont rejouées. */
export function resumeReadOnlyQueues(): void {
  resumeQueues(queues.values());
}

/** Files à relancer au retour de la licence ou de la session : en attente d'un nouvel essai, ou arrêtées par la
 *  lecture seule ou par la session de la personne. */
export function resumeQueues(list: Iterable<OrderSaveQueue>): void {
  for (const queue of list) {
    const { status, blocked } = queue.getSnapshot();
    if (status === "retrying" || (status === "blocked" && (blocked === "read_only" || blocked === "session"))) queue.retryNow();
    else queue.retryRestore();
  }
}

/** Arrêt de toutes les files sans rien envoyer (docs/17 §4.8 : « Changer d'utilisateur », session ouverte pour un
 *  autre compte) : ni nouvel essai, ni envoi en arrière-plan ou à la fermeture ; ce qui attend reste dans les copies
 *  locales, sous la portée de la personne, et lui sera rendu à son retour sur ce poste. Jusqu'au rechargement. */
let allHalted = false;
export function haltOrderQueues(): void {
  allHalted = true;
  for (const queue of queues.values()) queue.halt();
}

/** Avant un rechargement voulu (nouvelle version de Generex, audit m14) : la saisie en cours des éditeurs passe dans
 *  les files, chaque file envoie ce qu'elle attend, puis on attend qu'elles soient confirmées ou en attente sans
 *  nouvel essai, au plus `timeoutMs`. Rien n'est perdu au-delà : les champs non confirmés restent en copie locale sur
 *  ce poste et repartent à la réouverture de l'ordre. Rend vrai si tout a été confirmé. */
export async function flushOrderQueues(timeoutMs = 4000, now: () => number = Date.now): Promise<boolean> {
  for (const drain of drains) drain();
  for (const queue of queues.values()) {
    if (queue.getSnapshot().status === "retrying") queue.retryNow();
    else void queue.pump();
  }
  const settled = () =>
    [...queues.values()].every((q) => {
      const { status } = q.getSnapshot();
      return status === "saved" || status === "blocked";
    });
  const until = now() + timeoutMs;
  while (!settled() && now() < until) await new Promise((resolve) => setTimeout(resolve, 100));
  return [...queues.values()].every((q) => q.getSnapshot().status === "saved");
}

/** File d'enregistrement d'un ordre (créée au premier appel, gardée pour la session). */
export function orderSaveQueue(target: OrderTarget, queryClient: QueryClient): OrderSaveQueue {
  const id = `${target.exerciseId}:${target.number}`;
  let queue = queues.get(id);
  if (!queue) {
    queue = new OrderSaveQueue(target, appDeps(queryClient));
    if (allHalted) queue.halt();
    queues.set(id, queue);
    declare(queue);
    listen();
  }
  return queue;
}

/** Champs de l'ordre pas encore confirmés : déclarés au registre des saisies non enregistrées (audit de persistance
 *  M4), où que soit l'utilisateur. La file les garde sur le poste et continue d'elle-même : ils ne retiennent pas la
 *  navigation ; la fermeture de l'onglet demande confirmation tant qu'un envoi est en cours ou en attente. */
function declare(queue: OrderSaveQueue): void {
  const id = `order:${queue.target.exerciseId}:${queue.target.number}`;
  const sync = () => {
    const pending = Object.keys(queue.getSnapshot().unconfirmed).length > 0;
    if (!pending) unsavedDrafts.delete(id);
    else if (!unsavedDrafts.has(id)) {
      unsavedDrafts.set(id, {
        get label() {
          // OPLAN du corps : document n° 0 du dossier (docs/08 §25).
          return queue.target.number === 0 ? t("Plan d'opération (OPLAN)") : t("Ordre d'opérations N°{n}", { n: queue.target.number });
        },
        navigation: false,
        get unload() {
          const status = queue.getSnapshot().status;
          return status === "saving" || status === "retrying";
        },
        snapshot: drainOrderEditors,
      });
    }
  };
  queue.subscribe(sync);
}

const reason = (err: unknown) => (err instanceof Error && err.message ? err.message : undefined);

function appDeps(queryClient: QueryClient): QueueDeps {
  const notify = useUi.getState().notify;
  return {
    send: (target, version, fields, keepalive) =>
      api.editOrder(target.exerciseId, version, fields, target.number, keepalive),
    load: (target) => api.order(target.exerciseId, target.number),
    update: (target, order) => {
      const key = ["order", target.exerciseId, target.number];
      const cached = queryClient.getQueryData<Order>(key);
      // Réponse tardive (keepalive, relecture) : jamais d'ordre plus ancien que celui déjà affiché.
      if (cached && cached.id === order.id && cached.version > order.version) return;
      queryClient.setQueryData(key, order);
    },
    failed: (queue, err, blocked) => {
      if (blocked === "finalized") {
        notify({
          text: queue.target.number === 0 ? t("OPLAN validé : modification non enregistrée") : t("OPO validé : modification non enregistrée"),
          detail: t("Rouvrez l'ordre : votre texte, gardé sur ce poste, sera enregistré aussitôt."),
          tone: "warn",
        });
      } else if (blocked === "invalid") {
        notify({ text: t("Paragraphe non enregistré"), detail: reason(err), tone: "warn" });
      } else if (blocked === "read_only" || blocked === "session") {
        // Licence expirée : expliquée une fois et désignée par le bandeau de la lecture seule (`ReadOnlyLicence`).
        // Session verrouillée, fermée ou expirée : la surcouche des comptes le dit.
      } else if (blocked === "refused") {
        notify({
          text: t("Ordre non enregistré"),
          detail: reason(err) ?? t("Ce dossier est en lecture seule pour vous."),
          tone: "warn",
        });
      } else if (blocked === "gone") {
        notify({
          text: t("Ordre introuvable : modification non enregistrée"),
          detail: t("Le dossier a peut-être été supprimé ou n'est plus dans cet espace ; votre texte reste gardé sur ce poste."),
          tone: "warn",
        });
      } else {
        notify({
          text: t("Ordre non enregistré pour l'instant"),
          detail: t("Votre texte est gardé sur ce poste ; nouvel essai automatique."),
          tone: "warn",
          action: { label: t("Réessayer"), run: () => queue.retryNow() },
        });
      }
    },
    recovered: () => notify({ text: t("Modifications de l'ordre enregistrées"), tone: "ok" }),
    restored: (_queue, count) =>
      notify({
        text: t("Saisie de l'ordre retrouvée"),
        detail: t("Le texte non enregistré à la dernière fermeture est rétabli et enregistré ({count}).", {
          count: plural(count, t("paragraphe"), t("paragraphes")),
        }),
        tone: "info",
      }),
    storage: browserStore,
  };
}
